/* ==========================================================================
   GALLERY3D — three.js walk-through engine for MLOW · The Salon
   Builds a themed hall per room, hangs framed art on the walls with
   spotlights, dolly/look navigation, raycast collect. Drives window.Shop.
   ========================================================================== */
import * as THREE from 'three';

const ROOMS=window.ROOM3D, ORDER=window.ROOM_ORDER, ART=window.ART;
const ART_BY_ROOM={}; ART.forEach(a=>(ART_BY_ROOM[a.room]=ART_BY_ROOM[a.room]||[]).push(a));

/* ---------- geometry constants ---------- */
const WALL_X=4.9, WALL_H=4.9, EYE=1.62, ART_CY=2.0;
const SPACING=4.9, START_PAD=5.0, STANDOFF=5.0;

/* ---------- helpers ---------- */
const hex=c=>new THREE.Color(c);
function shadeHex(hexc,p){ const n=parseInt(hexc.slice(1),16); let r=(n>>16)&255,g=(n>>8)&255,b=n&255; const f=v=>Math.max(0,Math.min(255,Math.round(v+v*p/100))); return '#'+[f(r),f(g),f(b)].map(v=>v.toString(16).padStart(2,'0')).join(''); }
function lerp(a,b,t){ return a+(b-a)*t; }
function easeInOut(t){ return t<.5?2*t*t:1-Math.pow(-2*t+2,2)/2; }

/* ---------- procedural textures ---------- */
const _texCache={};
function makeTex(key,size,draw,repeatX,repeatY){
  if(_texCache[key]) return _texCache[key];
  const c=document.createElement('canvas'); c.width=c.height=size||512;
  draw(c.getContext('2d'),c.width,c.height);
  const t=new THREE.CanvasTexture(c);
  t.wrapS=t.wrapT=THREE.RepeatWrapping; t.repeat.set(repeatX||1,repeatY||1);
  t.anisotropy=8; _texCache[key]=t; return t;
}
function noise(ctx,w,h,amt,col){ const id=ctx.getImageData(0,0,w,h),d=id.data;
  for(let i=0;i<d.length;i+=4){ const n=(Math.random()-.5)*amt; d[i]+=n;d[i+1]+=n;d[i+2]+=n; } ctx.putImageData(id,0,0); }
function fill(ctx,w,h,col){ ctx.fillStyle=col; ctx.fillRect(0,0,w,h); }

function floorTexture(kind,theme){
  const base=theme.floor;
  switch(kind){
    case 'tatami': return makeTex('tatami',512,(x,w,h)=>{ fill(x,w,h,'#cdbb8e');
      x.strokeStyle='#b6a274'; x.lineWidth=3; for(let i=0;i<=4;i++){ const p=i*w/4; x.strokeRect(p,0,w/4,h);} 
      x.strokeStyle='#c2af82'; x.lineWidth=1; for(let i=0;i<h;i+=7){ x.beginPath();x.moveTo(0,i);x.lineTo(w,i);x.stroke(); } noise(x,w,h,14); },6,6);
    case 'wood': return makeTex('wood',512,(x,w,h)=>{ fill(x,w,h,base);
      for(let p=0;p<w;p+=64){ const c=p%128===0?'#6b4e2e':'#74553a'; fill2(x,p,0,64,h,c);
        x.strokeStyle='rgba(40,26,12,.4)';x.lineWidth=2;x.strokeRect(p,0,64,h);
        x.strokeStyle='rgba(90,66,38,.4)';x.lineWidth=1; for(let g=0;g<h;g+=14){x.beginPath();x.moveTo(p,g+Math.sin(p+g)*3);x.lineTo(p+64,g);x.stroke();}}
      noise(x,w,h,12); },2,4);
    case 'parquet': return makeTex('parquet',512,(x,w,h)=>{ fill(x,w,h,'#b08a55'); const s=w/4;
      for(let r=0;r<4;r++)for(let cc=0;cc<4;cc++){ const px=cc*s,py=r*s,horiz=(r+cc)%2===0;
        fill2(x,px,py,s,s, horiz?'#a8824f':'#b89160');
        x.strokeStyle='rgba(60,40,18,.45)';x.lineWidth=1.5;
        for(let i=0;i<5;i++){ if(horiz){x.beginPath();x.moveTo(px,py+i*s/5);x.lineTo(px+s,py+i*s/5);x.stroke();}else{x.beginPath();x.moveTo(px+i*s/5,py);x.lineTo(px+i*s/5,py+s);x.stroke();}}
        x.strokeStyle='rgba(40,26,12,.5)';x.strokeRect(px,py,s,s);} noise(x,w,h,10); },3,3);
    case 'stone': return makeTex('stone',512,(x,w,h)=>{ fill(x,w,h,'#e6ddca'); noise(x,w,h,16);
      x.strokeStyle='rgba(150,135,110,.5)';x.lineWidth=2; for(let i=0;i<=4;i++){x.beginPath();x.moveTo(i*w/4,0);x.lineTo(i*w/4,h);x.stroke();x.beginPath();x.moveTo(0,i*h/4);x.lineTo(w,i*h/4);x.stroke();} },4,4);
    case 'marble': return makeTex('marble',512,(x,w,h)=>{ fill(x,w,h,'#241433'); 
      for(let i=0;i<7;i++){ x.strokeStyle='rgba(180,150,210,'+(.1+Math.random()*.16)+')';x.lineWidth=1+Math.random()*2;x.beginPath();let yy=Math.random()*h;x.moveTo(0,yy);for(let xx=0;xx<w;xx+=20){yy+=(Math.random()-.5)*40;x.lineTo(xx,yy);}x.stroke();} noise(x,w,h,8); },3,3);
    case 'zellige': return makeTex('zellige',512,(x,w,h)=>{ fill(x,w,h,'#0b3346'); const s=w/8;
      for(let r=0;r<8;r++)for(let cc=0;cc<8;cc++){ const cols=['#0d3c52','#12597a','#0a2c3d','#1d7aa0'];
        fill2(x,cc*s,r*s,s,s,cols[(r+cc)%cols.length]); x.strokeStyle='rgba(201,162,74,.5)';x.lineWidth=2;x.strokeRect(cc*s+1,r*s+1,s-2,s-2);} },4,4);
    case 'velvet': return makeTex('velvet',512,(x,w,h)=>{ fill(x,w,h,'#24090e'); noise(x,w,h,18);
      x.fillStyle='rgba(120,20,35,.18)'; for(let i=0;i<40;i++){x.beginPath();x.arc(Math.random()*w,Math.random()*h,30+Math.random()*60,0,7);x.fill();} },3,3);
    case 'metal': return makeTex('metal',512,(x,w,h)=>{ fill(x,w,h,'#0d101e'); x.strokeStyle='rgba(120,140,190,.10)';x.lineWidth=1;
      for(let i=0;i<h;i+=3){x.beginPath();x.moveTo(0,i);x.lineTo(w,i);x.stroke();}
      x.strokeStyle='rgba(60,75,120,.5)';x.lineWidth=2; for(let i=0;i<=8;i++){x.strokeRect(i*w/8,0,w/8,h);} },3,3);
    case 'mirror': return makeTex('mirror',256,(x,w,h)=>{ fill(x,w,h,'#050509'); },1,1);
    case 'grid': return makeTex('grid',512,(x,w,h)=>{ fill(x,w,h,'#10031f');
      x.strokeStyle='rgba(255,46,99,.5)';x.lineWidth=2; for(let i=0;i<=8;i++){x.beginPath();x.moveTo(i*w/8,0);x.lineTo(i*w/8,h);x.stroke();x.beginPath();x.moveTo(0,i*h/8);x.lineTo(w,i*h/8);x.stroke();}
      x.strokeStyle='rgba(0,229,255,.35)';x.lineWidth=1; for(let i=0;i<=16;i++){x.beginPath();x.moveTo(i*w/16,0);x.lineTo(i*w/16,h);x.stroke();} },2,3);
    case 'plank': return makeTex('plank',512,(x,w,h)=>{ fill(x,w,h,base);
      for(let p=0;p<h;p+=70){ fill2(x,0,p,w,70,p%140===0?shadeHex(base,-8):shadeHex(base,6)); x.strokeStyle='rgba(0,0,0,.3)';x.lineWidth=2;x.strokeRect(0,p,w,70);
        x.strokeStyle='rgba(255,255,255,.04)';x.lineWidth=1; for(let g=0;g<w;g+=20){x.beginPath();x.moveTo(g,p+Math.sin(g)*2);x.lineTo(g,p+70);x.stroke();}} noise(x,w,h,8); },2,3);
    case 'concrete': return makeTex('concrete',512,(x,w,h)=>{ fill(x,w,h,base); noise(x,w,h,10);
      x.strokeStyle='rgba(0,0,0,.08)';x.lineWidth=2; x.strokeRect(0,0,w,h); x.beginPath();x.moveTo(w/2,0);x.lineTo(w/2,h);x.moveTo(0,h/2);x.lineTo(w,h/2);x.stroke(); },3,3);
    case 'flagstone': return makeTex('flagstone',512,(x,w,h)=>{ fill(x,w,h,base);
      x.strokeStyle='rgba(0,0,0,.34)';x.lineWidth=4; const s=w/4;
      for(let r=0;r<5;r++)for(let c=0;c<4;c++){ const off=r%2?s/2:0; fill2(x,c*s+off-s/2,r*h/5,s,h/5, r%2?shadeHex(base,5):shadeHex(base,-5)); x.strokeRect(c*s+off-s/2,r*h/5,s,h/5);} noise(x,w,h,12); },3,3);
    case 'moss': return makeTex('moss',512,(x,w,h)=>{ fill(x,w,h,base); 
      for(let i=0;i<400;i++){ x.fillStyle='rgba('+(40+Math.random()*60|0)+','+(90+Math.random()*70|0)+','+(40+Math.random()*40|0)+',.5)'; x.beginPath(); x.arc(Math.random()*w,Math.random()*h,2+Math.random()*5,0,7); x.fill(); }
      for(let i=0;i<30;i++){ x.fillStyle='rgba(120,160,80,.4)'; x.beginPath(); x.arc(Math.random()*w,Math.random()*h,8+Math.random()*16,0,7); x.fill(); } },4,4);
    case 'caustic': return makeTex('caustic',512,(x,w,h)=>{ fill(x,w,h,'#062f50');
      for(let i=0;i<60;i++){ const cx=Math.random()*w,cy=Math.random()*h,r=18+Math.random()*44; const g=x.createRadialGradient(cx,cy,0,cx,cy,r); g.addColorStop(0,'rgba(140,210,255,.16)');g.addColorStop(1,'rgba(140,210,255,0)');x.fillStyle=g;x.beginPath();x.arc(cx,cy,r,0,7);x.fill();} },4,4);
    case 'deco': return makeTex('deco',512,(x,w,h)=>{ fill(x,w,h,'#eef3f4');
      const cols=['#ff8fb0','#7fd8f0','#ffd98a','#b6e3c8']; for(let i=0;i<120;i++){x.fillStyle=cols[i%cols.length];x.globalAlpha=.5;x.beginPath();x.arc(Math.random()*w,Math.random()*h,4+Math.random()*9,0,7);x.fill();} x.globalAlpha=1; },4,4);
    default: return makeTex('plain_'+base,64,(x,w,h)=>fill(x,w,h,base),3,3);
  }
}
function fill2(ctx,x,y,w,h,c){ ctx.fillStyle=c; ctx.fillRect(x,y,w,h); }

function wallTexture(theme){
  return makeTex('wall_'+theme.wall,256,(x,w,h)=>{ fill(x,w,h,theme.wall);
    // subtle vertical panels
    x.strokeStyle='rgba(0,0,0,.05)';x.lineWidth=2; for(let i=0;i<=4;i++){x.beginPath();x.moveTo(i*w/4,0);x.lineTo(i*w/4,h);x.stroke();}
    noise(x,w,h,6); },2,1);
}

/* ---------- label plaque ---------- */
function plaqueTexture(a,dark){
  const c=document.createElement('canvas'); c.width=512; c.height=160;
  const x=c.getContext('2d');
  x.fillStyle=dark?'#15161c':'#f3efe6'; x.fillRect(0,0,512,160);
  x.fillStyle=dark?'#ffffff':'#0D0D0D';
  x.font='600 38px Georgia, serif'; x.textBaseline='top';
  let t=a.title; if(t.length>26) t=t.slice(0,25)+'…';
  x.fillText(t,22,24);
  x.fillStyle=dark?'#9aa4b2':'#6b6256'; x.font='500 24px monospace';
  x.fillText(a.collName+' · '+a.id,22,78);
  x.fillStyle=dark?'#c7cedb':'#6b6256'; x.font='600 24px monospace';
  x.fillText('Limited edition · signed & numbered',22,116);
  const t2=new THREE.CanvasTexture(c); t2.anisotropy=8; return t2;
}

/* ---------- resilient texture loader ---------- */
const _loader=new THREE.TextureLoader(); _loader.setCrossOrigin('anonymous');
function ipfsPath(url){ let m=url.match(/ipfs\/([^&?]+)/); return m?m[1]:null; }
function candidates(url){ const p=ipfsPath(url); if(!p) return [url];
  return ['https://ipfs.io/ipfs/'+p,
    'https://images.weserv.nl/?url=ssl:ipfs.io/ipfs/'+p+'&w=1000&output=jpg&q=80',
    'https://dweb.link/ipfs/'+p]; }
function loadTex(url){ return new Promise(res=>{ const list=candidates(url); let i=0, done=false;
  const finish=v=>{ if(done) return; done=true; res(v); };
  const tryNext=()=>{ if(done) return; if(i>=list.length){ finish(null); return; }
    const cur=list[i++]; let advanced=false;
    // If a gateway hangs (no error, no load) move on after 6s so one slow node can't stall a piece for minutes.
    const to=setTimeout(()=>{ if(!advanced){ advanced=true; tryNext(); } }, 6000);
    _loader.load(cur,t=>{ clearTimeout(to); if(advanced) return; t.colorSpace=THREE.SRGBColorSpace; t.anisotropy=8; finish(t); },
      undefined, ()=>{ clearTimeout(to); if(!advanced){ advanced=true; tryNext(); } }); };
  tryNext(); }); }

/* ==========================================================================
   ENGINE
   ========================================================================== */
class Gallery3D{
  constructor(canvas){
    this.canvas=canvas;
    this.renderer=new THREE.WebGLRenderer({canvas,antialias:true,powerPreference:'high-performance'});
    this.renderer.setPixelRatio(Math.min(devicePixelRatio,2));
    this.renderer.shadowMap.enabled=true; this.renderer.shadowMap.type=THREE.PCFSoftShadowMap;
    this.renderer.toneMapping=THREE.ACESFilmicToneMapping; this.renderer.toneMappingExposure=1.05;
    this.scene=new THREE.Scene();
    this.camera=new THREE.PerspectiveCamera(58,1,0.1,200);
    this.pmrem=new THREE.PMREMGenerator(this.renderer);
    this.clock=new THREE.Clock();
    this.roomGroup=null; this.artGroups=[]; this.artMeshes=[];
    this.focus=0; this.tween=null;
    this.camPos=new THREE.Vector3(); this.camTarget=new THREE.Vector3();
    this.baseYaw=0; this.basePitch=0; this.dragYaw=0; this.dragPitch=0;
    this.dragVel={x:0,y:0};
    this.spot=null; this.starField=null;
    this.interactives=[]; this.animated=[];
    this._raf=null;
    this._resize=this._resize.bind(this);
    this._animate=this._animate.bind(this);
    window.addEventListener('resize',this._resize);
    this._bindInput();
    this._resize();
  }

  /* ---- environment from theme colors ---- */
  _envFromTheme(theme){
    const c=document.createElement('canvas'); c.width=256;c.height=128;
    const x=c.getContext('2d'); const g=x.createLinearGradient(0,0,0,128);
    g.addColorStop(0,theme.ceiling); g.addColorStop(0.5,theme.wall); g.addColorStop(1,theme.floor);
    x.fillStyle=g; x.fillRect(0,0,256,128);
    // accent glow band
    x.fillStyle=theme.accent; x.globalAlpha=.25; x.fillRect(0,40,256,24); x.globalAlpha=1;
    const tex=new THREE.CanvasTexture(c); tex.mapping=THREE.EquirectangularReflectionMapping;
    const env=this.pmrem.fromEquirectangular(tex).texture; tex.dispose();
    return env;
  }

  /* ---- build a room ---- */
  buildRoom(roomId){
    this._disposeRoom();
    const theme=ROOMS[roomId]; const arr=ART_BY_ROOM[roomId]||[];
    const g=new THREE.Group(); this.roomGroup=g; this.scene.add(g);

    // env + fog + sky
    this.scene.environment=this._envFromTheme(theme);
    this.scene.fog=new THREE.FogExp2(theme.fog[0], Math.min(theme.fog[1], theme.openCeiling?0.012:theme.fog[1]));
    this._buildSky(theme,g);

    // dispatch layout → builds geometry, returns {slots, bounds, size}
    const layout=theme.layout||'hall';
    const L=(this['_layout_'+layout]||this._layout_hall).call(this,theme,arr,g,roomId);
    this.slots=L.slots; this.bounds=L.bounds; this.roomLen=L.size; this._layout=L;

    // common lights + neutral focus spot
    this._commonLights(theme,g,L);
    this.spot=new THREE.SpotLight(0xffffff, 11, 14, Math.PI/7, 0.5, 1.3);
    this.spot.castShadow=true; this.spot.shadow.mapSize.set(1024,1024);
    this.spot.shadow.camera.near=0.5; this.spot.shadow.camera.far=14; this.spot.shadow.bias=-0.0008;
    g.add(this.spot); g.add(this.spot.target);

    // hang art at slots
    this.artGroups=[]; this.artMeshes=[];
    arr.forEach((a,i)=>{ const s=this.slots[i]; if(!s) return; const grp=this._buildArtAt(a,s,theme); g.add(grp); this.artGroups.push(grp); });

    // set-dressing (layout-aware)
    try{ window.Decor.dress(THREE, g, theme, roomId, this._ctx(g,L)); }catch(e){ console.warn('decor',e); }

    return arr;
  }

  /* ---- SKY DOME (day sun+clouds / sunset / night stars) ---- */
  _buildSky(theme,g){
    const mode=theme.sky||'none';
    const c=document.createElement('canvas'); c.width=1024; c.height=512; const x=c.getContext('2d');
    const grad=(stops)=>{ const gr=x.createLinearGradient(0,0,0,512); stops.forEach(([o,col])=>gr.addColorStop(o,col)); return gr; };
    if(mode==='day'){ x.fillStyle=grad([[0,'#2f6fc0'],[0.5,'#7fb4e8'],[1,'#cfe6f7']]); x.fillRect(0,0,1024,512);
      // sun
      x.fillStyle='#fff7d8'; x.beginPath(); x.arc(760,150,46,0,7); x.fill();
      const sg=x.createRadialGradient(760,150,10,760,150,160); sg.addColorStop(0,'rgba(255,247,216,.7)'); sg.addColorStop(1,'rgba(255,247,216,0)'); x.fillStyle=sg; x.beginPath(); x.arc(760,150,160,0,7); x.fill();
      // clouds
      x.fillStyle='rgba(255,255,255,.92)'; for(let i=0;i<10;i++){ const cx=Math.random()*1024, cy=60+Math.random()*200; for(let p=0;p<6;p++){ x.beginPath(); x.ellipse(cx+p*34-90,cy+Math.sin(p)*8,46,26,0,0,7); x.fill(); } }
    } else if(mode==='sunset'){ x.fillStyle=grad([[0,'#3a2f6a'],[0.4,'#9b5f7e'],[0.62,'#e0756a'],[0.8,'#f2a45c'],[1,'#ffd9a0']]); x.fillRect(0,0,1024,512);
      const sg=x.createRadialGradient(512,360,10,512,360,200); sg.addColorStop(0,'#fff3d0'); sg.addColorStop(.5,'#ffba5c'); sg.addColorStop(1,'rgba(255,186,92,0)'); x.fillStyle=sg; x.beginPath(); x.arc(512,360,200,0,7); x.fill();
      x.fillStyle='rgba(80,50,80,.5)'; for(let i=0;i<8;i++){ const cx=Math.random()*1024,cy=80+Math.random()*160; for(let p=0;p<5;p++){ x.beginPath(); x.ellipse(cx+p*40-80,cy,52,18,0,0,7); x.fill(); } }
    } else if(mode==='dusk'){ x.fillStyle=grad([[0,'#1a2a52'],[0.5,'#5a6f9e'],[1,'#c9a98a']]); x.fillRect(0,0,1024,512);
      x.fillStyle='#fff'; for(let i=0;i<60;i++){ x.globalAlpha=Math.random()*0.7; x.beginPath(); x.arc(Math.random()*1024,Math.random()*200,Math.random()*1.4,0,7); x.fill(); } x.globalAlpha=1;
    } else { // night
      x.fillStyle=grad([[0,'#03030a'],[0.6,'#0a0a1e'],[1,'#12122e']]); x.fillRect(0,0,1024,512);
      x.fillStyle='#fff'; for(let i=0;i<260;i++){ x.globalAlpha=Math.random()*0.9+0.1; x.beginPath(); x.arc(Math.random()*1024,Math.random()*512,Math.random()*1.6,0,7); x.fill(); } x.globalAlpha=1;
      // moon
      x.fillStyle='#eef0ff'; x.beginPath(); x.arc(800,120,40,0,7); x.fill();
      const mg=x.createRadialGradient(800,120,10,800,120,120); mg.addColorStop(0,'rgba(200,210,255,.5)'); mg.addColorStop(1,'rgba(200,210,255,0)'); x.fillStyle=mg; x.beginPath(); x.arc(800,120,120,0,7); x.fill();
    }
    const tex=new THREE.CanvasTexture(c); tex.colorSpace=THREE.SRGBColorSpace; tex.mapping=THREE.EquirectangularReflectionMapping;
    const sky=new THREE.Mesh(new THREE.SphereGeometry(160,40,24), new THREE.MeshBasicMaterial({map:tex,side:THREE.BackSide,fog:false}));
    sky.position.y=0; g.add(sky); this._sky=sky;
    if(mode==='none'){ sky.visible=false; this.scene.background=hex(theme.fog[0]); }
    else this.scene.background=hex(mode==='night'||mode==='dusk'?'#06060f':theme.fog[0]);
    // drifting clouds rotation
    if(mode==='day'||mode==='sunset') this.animated.push((dt)=>{ sky.rotation.y+=dt*0.004; });
    else this.animated.push((dt)=>{ sky.rotation.y+=dt*0.001; });
  }

  /* ---- common lights ---- */
  _commonLights(theme,g,L){
    const [ambC,ambI]=theme.ambient, [keyC,keyI]=theme.key;
    g.add(new THREE.HemisphereLight(hex(theme.openCeiling?'#bcd6f5':theme.ceiling),hex(theme.floor),ambI*(theme.openCeiling?0.9:0.7)));
    g.add(new THREE.AmbientLight(hex(ambC),ambI*0.5));
    const key=new THREE.DirectionalLight(hex(theme.openCeiling?'#fff4e0':keyC),keyI*(theme.openCeiling?0.8:0.5));
    key.position.set(6,14,L.size*0.3); g.add(key);
  }

  // context handed to the decor library
  _ctx(g,L){
    const self=this;
    return {
      g, length:L.size, size:L.size, layout:L.layout, bounds:L.bounds, center:L.center, slots:L.slots,
      WALL_X, WALL_H, EYE, ART_CY, START_PAD, SPACING, hex, shadeHex,
      Shop:window.Shop, room:this.curRoom,
      interactive(mesh,fn){ mesh.userData.onClick=fn; self.interactives.push(mesh); },
      animate(fn){ self.animated.push(fn); },
      setEgg(mesh){ self.eggMesh=mesh; }
    };
  }

  /* ================= LAYOUT BUILDERS ================= */
  _matFloor(theme,ru,rv){
    const t=floorTexture(theme.floorKind,theme); t.repeat.set(ru,rv);
    const refl=theme.floorKind==='mirror'||theme.floorKind==='marble'||theme.floorKind==='metal';
    if(theme.floorKind==='caustic') this._causticTex=t;
    return new THREE.MeshStandardMaterial({map:t,color:hex(theme.floor),roughness:refl?0.14:0.85,metalness:refl?0.8:0.05,envMapIntensity:refl?1.2:0.4});
  }
  _matWall(theme){ const t=wallTexture(theme); t.repeat.set(3,1);
    return new THREE.MeshStandardMaterial({map:t,color:hex(theme.wall),roughness:.92,metalness:.02,envMapIntensity:.5}); }
  _mural(roomId,theme){ const tex=new THREE.CanvasTexture(window.Backdrops.paint(roomId,theme));
    tex.colorSpace=THREE.SRGBColorSpace; tex.anisotropy=8; return new THREE.MeshBasicMaterial({map:tex}); }
  _ceiling(theme,g,geo,y){ if(theme.openCeiling) return; const m=new THREE.Mesh(geo,new THREE.MeshStandardMaterial({color:hex(theme.ceiling),roughness:1}));
    m.rotation.x=Math.PI/2; m.position.y=y; g.add(m); }

  _layout_hall(theme,arr,g,roomId){ return this._hallLike(theme,arr,g,roomId, 4.9); }
  _layout_wide(theme,arr,g,roomId){ return this._hallLike(theme,arr,g,roomId, 7.4); }
  _hallLike(theme,arr,g,roomId,WX){
    const perWall=Math.ceil(arr.length/2);
    const size=START_PAD+Math.max(0,perWall-1)*SPACING+START_PAD;
    // floor
    const floor=new THREE.Mesh(new THREE.PlaneGeometry(WX*2,size),this._matFloor(theme,4,size/3));
    floor.rotation.x=-Math.PI/2; floor.position.set(0,0,size/2); floor.receiveShadow=true; g.add(floor);
    this._ceiling(theme,g,new THREE.PlaneGeometry(WX*2,size),WALL_H);
    // side walls
    const wm=this._matWall(theme);
    [[-WX,Math.PI/2],[WX,-Math.PI/2]].forEach(([xp,ry])=>{ const m=new THREE.Mesh(new THREE.PlaneGeometry(size,WALL_H),wm.clone()); m.material.map=wm.map.clone(); m.material.map.repeat.set(size/4,1); m.position.set(xp,WALL_H/2,size/2); m.rotation.y=ry; m.receiveShadow=true; g.add(m); });
    // end murals
    const mm=this._mural(roomId,theme); const mw=WX*2, mh=Math.min(WALL_H*0.86, mw*(720/1280));
    [[0.04,0],[size-0.04,Math.PI]].forEach(([z,ry])=>{ const back=new THREE.Mesh(new THREE.PlaneGeometry(mw,WALL_H),new THREE.MeshStandardMaterial({color:hex(theme.wainscot),roughness:.95})); back.position.set(0,WALL_H/2,z); back.rotation.y=ry; g.add(back);
      const mu=new THREE.Mesh(new THREE.PlaneGeometry(mw*0.94,mh),mm); mu.position.set(0,Math.max(mh/2+0.2,WALL_H*0.5),z+(ry?-0.04:0.04)); mu.rotation.y=ry; g.add(mu); });
    const mglow=new THREE.PointLight(hex(theme.key[0]),0.45,16); mglow.position.set(0,WALL_H*0.6,size-2.5); g.add(mglow);
    // wainscot
    const wain=new THREE.MeshStandardMaterial({color:hex(theme.wainscot),roughness:.8,metalness:.1});
    [[-WX,1],[WX,-1]].forEach(([xp,s])=>{ const b=new THREE.Mesh(new THREE.BoxGeometry(size,0.5,0.08),wain); b.position.set(xp- s*0.04,0.25,size/2); b.rotation.y=s>0?Math.PI/2:-Math.PI/2; g.add(b); });
    // slots: alternate walls, but spread each wall's own pieces evenly across the
    // full room length so an uneven (e.g. odd) count never leaves one wall blank at the back.
    const cnt0=Math.ceil(arr.length/2), cnt1=arr.length-cnt0;
    const span=Math.max(0,size-2*START_PAD);
    const slots=[]; arr.forEach((a,i)=>{ const side=i%2,row=Math.floor(i/2);
      const wallCnt=side===0?cnt0:cnt1; const x=side===0?-WX+0.06:WX-0.06;
      const z=wallCnt<=1 ? size/2 : START_PAD+row*(span/(wallCnt-1));
      slots.push({x,y:ART_CY,z, ry:side===0?Math.PI/2:-Math.PI/2, nx:side===0?1:-1, nz:0}); });
    return {slots, size, layout:theme.layout, center:new THREE.Vector3(0,0,size/2), WX,
      bounds:{type:'rect',minX:-WX+0.7,maxX:WX-0.7,minZ:1.1,maxZ:size-1.1}};
  }

  _layout_square(theme,arr,g,roomId){
    const n=arr.length, perWall=Math.max(1,Math.ceil(n/4));
    const S=Math.max(13, perWall*SPACING+5);
    const floor=new THREE.Mesh(new THREE.PlaneGeometry(S,S),this._matFloor(theme,5,5));
    floor.rotation.x=-Math.PI/2; floor.position.set(0,0,S/2); floor.receiveShadow=true; g.add(floor);
    this._ceiling(theme,g,new THREE.PlaneGeometry(S,S),WALL_H);
    const wm=this._matWall(theme);
    const walls=[[-S/2,S/2,Math.PI/2],[S/2,S/2,-Math.PI/2],[0,0,0],[0,S,Math.PI]]; // [x or -, z, ry]
    // build 4 walls
    [[0,S/2,-S/2,Math.PI/2],[0,S/2,S/2,-Math.PI/2],[0,0,0,0],[0,S,0,Math.PI]].forEach(()=>{});
    const mkWall=(x,z,ry,len)=>{ const m=new THREE.Mesh(new THREE.PlaneGeometry(len,WALL_H),wm.clone()); m.material.map=wm.map.clone(); m.material.map.repeat.set(len/4,1); m.position.set(x,WALL_H/2,z); m.rotation.y=ry; m.receiveShadow=true; g.add(m); };
    mkWall(-S/2,S/2,Math.PI/2,S); mkWall(S/2,S/2,-Math.PI/2,S); mkWall(0,0,0,S); mkWall(0,S,Math.PI,S);
    // mural on back wall (z=S) and entrance wall (z=0)
    const mm=this._mural(roomId,theme); const mh=Math.min(WALL_H*0.8, S*0.4*(720/1280));
    const mu=new THREE.Mesh(new THREE.PlaneGeometry(S*0.5,mh),mm); mu.position.set(0,WALL_H*0.55,S-0.05); mu.rotation.y=Math.PI; g.add(mu);
    // wainscot ring
    const wain=new THREE.MeshStandardMaterial({color:hex(theme.wainscot),roughness:.8,metalness:.1});
    [[-S/2,S/2,Math.PI/2],[S/2,S/2,-Math.PI/2],[0,0.04,0],[0,S-0.04,Math.PI]].forEach(([x,z,ry])=>{ const b=new THREE.Mesh(new THREE.BoxGeometry(S,0.5,0.08),wain); b.position.set(x,0.25,z); b.rotation.y=ry; g.add(b); });
    // distribute art on 4 walls
    const slots=[]; const wallsDef=[
      {ax:'x',sign:-1,fixed:-S/2+0.06, ry:Math.PI/2, nx:1,nz:0},   // left
      {ax:'x',sign:1, fixed:S/2-0.06,  ry:-Math.PI/2,nx:-1,nz:0},  // right
      {ax:'z',sign:1, fixed:S-0.06,    ry:Math.PI,   nx:0,nz:-1},  // back
      {ax:'z',sign:-1,fixed:0.06,      ry:0,         nx:0,nz:1}    // front/entrance
    ];
    arr.forEach((a,i)=>{ const w=wallsDef[i%4]; const idxOnWall=Math.floor(i/4); const cnt=Math.ceil((n-(i%4))/4);
      // Keep art clear of the corners: inset each wall by ~half a frame + margin so
      // pieces never collide with the perpendicular wall's art or clip the corner.
      const CORNER=2.7; const span=Math.max(SPACING, S-2*CORNER);
      const t=cnt<=1?0.5:(idxOnWall/(cnt-1)); const along=-span/2+span*t;
      let x,z; if(w.ax==='x'){ x=w.fixed; z=S/2+along; } else { z=w.fixed; x=along; }
      slots.push({x,y:ART_CY,z,ry:w.ry,nx:w.nx,nz:w.nz}); });
    return {slots,size:S,layout:'square',center:new THREE.Vector3(0,0,S/2),WX:S/2,
      bounds:{type:'rect',minX:-S/2+0.7,maxX:S/2-0.7,minZ:0.8,maxZ:S-0.8}};
  }

  _layout_rotunda(theme,arr,g,roomId){
    const n=arr.length; const R=Math.max(7, n*0.95+2.5); const cz=R+0.5;
    // floor disc
    const floor=new THREE.Mesh(new THREE.CircleGeometry(R,48),this._matFloor(theme,4,4));
    floor.rotation.x=-Math.PI/2; floor.position.set(0,0,cz); floor.receiveShadow=true; g.add(floor);
    // circular wall (inside)
    const wm=this._matWall(theme); const wt=wm.map.clone(); wt.wrapS=wt.wrapT=THREE.RepeatWrapping; wt.repeat.set(R,1);
    const wall=new THREE.Mesh(new THREE.CylinderGeometry(R,R,WALL_H,64,1,true),new THREE.MeshStandardMaterial({map:wt,color:hex(theme.wall),side:THREE.BackSide,roughness:.92}));
    wall.position.set(0,WALL_H/2,cz); g.add(wall);
    // ceiling: open oculus to sky OR dome
    if(!theme.openCeiling){ const dome=new THREE.Mesh(new THREE.SphereGeometry(R,40,16,0,Math.PI*2,0,Math.PI/2),new THREE.MeshStandardMaterial({color:hex(theme.ceiling),side:THREE.BackSide,roughness:1})); dome.position.set(0,WALL_H-0.2,cz); g.add(dome); }
    else { // ring rim around oculus
      const rim=new THREE.Mesh(new THREE.TorusGeometry(R*0.62,0.2,8,40),new THREE.MeshStandardMaterial({color:hex(theme.wainscot),roughness:.7})); rim.rotation.x=Math.PI/2; rim.position.set(0,WALL_H-0.1,cz); g.add(rim); }
    // wainscot ring
    const wr=new THREE.Mesh(new THREE.TorusGeometry(R-0.05,0.16,8,48),new THREE.MeshStandardMaterial({color:hex(theme.wainscot),roughness:.8})); wr.rotation.x=Math.PI/2; wr.position.set(0,0.25,cz); g.add(wr);
    // slots around the curve (skip a gap near the entrance at south, angle -PI/2)
    const slots=[]; const gap=0.6; const span=Math.PI*2-gap*2;
    arr.forEach((a,i)=>{ const t=n<=1?0.5:i/n; const ang=-Math.PI/2+gap+span*t;
      const x=Math.cos(ang)*(R-0.1), z=cz+Math.sin(ang)*(R-0.1);
      const nx=-Math.cos(ang), nz=-Math.sin(ang); // inward normal toward centre
      slots.push({x,y:ART_CY,z, ry:Math.atan2(nx,nz), nx, nz}); });
    return {slots,size:cz*2,layout:'rotunda',center:new THREE.Vector3(0,0,cz),WX:R,
      bounds:{type:'circle',cx:0,cz,r:R-1.0}};
  }

  _addStars(g,length){
    const n=600, pos=new Float32Array(n*3);
    for(let i=0;i<n;i++){ pos[i*3]=(Math.random()-.5)*WALL_X*2*0.95; pos[i*3+1]=Math.random()*WALL_H; pos[i*3+2]=Math.random()*length; }
    const geo=new THREE.BufferGeometry(); geo.setAttribute('position',new THREE.BufferAttribute(pos,3));
    const mat=new THREE.PointsMaterial({color:0xffffff,size:0.03,transparent:true,opacity:.8});
    this.starField=new THREE.Points(geo,mat); g.add(this.starField);
  }

  _buildArtAt(a,slot,theme){
    const grp=new THREE.Group(); grp.position.set(slot.x,ART_CY,slot.z); grp.rotation.y=slot.ry;
    grp.userData={art:a,slot};

    const dark=theme.mat==='dark';
    const frameColor=({black:'#161616',white:'#f2efe8',gold:'#c9a24a',neon:'#0a0a12'})[theme.frame];
    const frameMat=new THREE.MeshStandardMaterial({color:hex(frameColor),
      roughness:theme.frame==='gold'?0.32:0.55, metalness:theme.frame==='gold'?0.85:0.2,
      emissive: theme.frame==='neon'?hex(theme.accent):hex('#000'), emissiveIntensity: theme.frame==='neon'?0.7:0 });
    const matMat=new THREE.MeshStandardMaterial({color:hex(dark?'#14161c':'#f3efe6'),roughness:.95,metalness:0});
    // Artwork plane reads as UNLIT but stays a MeshStandardMaterial so the renderer's
    // program always carries an emissiveMap uniform (a bare MeshBasicMaterial with .map
    // crashes refreshUniformsCommon in three r160). Trick: diffuse colour black (so scene
    // lights / the per-piece spotlight contribute nothing) and the texture is driven through
    // the EMISSIVE channel at full intensity; toneMapped:false bypasses ACES tone mapping.
    // Result is identical to the detail/lightbox image — true sRGB, no hotspot, no tint.
    const artMat=new THREE.MeshStandardMaterial({color:hex('#000'),emissive:hex('#3a3c40'),
      emissiveIntensity:1, roughness:1, metalness:0, toneMapped:false});

    const frameMesh=new THREE.Mesh(new THREE.BoxGeometry(1,1,0.14),frameMat); frameMesh.castShadow=true; frameMesh.receiveShadow=true;
    const matMesh=new THREE.Mesh(new THREE.PlaneGeometry(1,1),matMat);
    const artMesh=new THREE.Mesh(new THREE.PlaneGeometry(1,1),artMat);
    frameMesh.position.z=0.0; matMesh.position.z=0.075; artMesh.position.z=0.085;
    grp.add(frameMesh,matMesh,artMesh);

    // plaque
    const plaqueMat=new THREE.MeshStandardMaterial({map:plaqueTexture(a,dark),roughness:.8,metalness:.05,transparent:true});
    const plaque=new THREE.Mesh(new THREE.PlaneGeometry(0.62,0.194),plaqueMat);
    plaque.position.set(0,-1.42,0.09); grp.add(plaque);

    grp.userData.frameMesh=frameMesh; grp.userData.matMesh=matMesh; grp.userData.artMesh=artMesh; grp.userData.plaque=plaque;
    artMesh.userData.artGroup=grp; this.artMeshes.push(artMesh); frameMesh.userData.artGroup=grp; this.artMeshes.push(frameMesh);

    this._layoutArt(grp, 0.8, theme); // placeholder portrait

    // picture light — brass fixture + NEUTRAL wash so every piece reads true colour
    const brass=new THREE.MeshStandardMaterial({color:hex('#c9a86a'),metalness:.85,roughness:.3,emissive:hex('#3a2e16'),emissiveIntensity:.25});
    const arm=new THREE.Mesh(new THREE.CylinderGeometry(0.018,0.018,0.42,8),brass);
    arm.position.set(0,1.55,0.16); arm.rotation.x=0.6; grp.add(arm);
    const shade=new THREE.Mesh(new THREE.BoxGeometry(0.7,0.07,0.14),brass);
    shade.position.set(0,1.72,0.34); grp.add(shade);
    const pic=new THREE.SpotLight(0xffffff, 2.4, 6.5, Math.PI/3.4, 0.7, 1.1);
    pic.position.set(0,1.68,0.34); grp.add(pic);
    const tgt=new THREE.Object3D(); tgt.position.set(0,-0.1,0.02); grp.add(tgt); pic.target=tgt;

    // load texture
    loadTex(a.thumb).then(tex=>{ if(!tex){ artMat.emissive=hex(theme.accent); artMat.needsUpdate=true; return; }
      artMat.emissiveMap=tex; artMat.emissive=hex('#fff'); artMat.needsUpdate=true;
      const ar=(tex.image.width||1)/(tex.image.height||1); this._layoutArt(grp,ar,theme);
    });
    return grp;
  }

  _layoutArt(grp,ar,theme){
    const dark=theme.mat==='dark';
    let artH=1.9, artW=artH*ar; const maxW=2.7;
    if(artW>maxW){ artW=maxW; artH=artW/ar; }
    const mb=dark?0.12:0.17, fb=0.11;
    const matW=artW+2*mb, matH=artH+2*mb, frW=matW+2*fb, frH=matH+2*fb;
    const {frameMesh,matMesh,artMesh,plaque}=grp.userData;
    frameMesh.geometry.dispose(); frameMesh.geometry=new THREE.BoxGeometry(frW,frH,0.14);
    matMesh.geometry.dispose(); matMesh.geometry=new THREE.PlaneGeometry(matW,matH);
    artMesh.geometry.dispose(); artMesh.geometry=new THREE.PlaneGeometry(artW,artH);
    plaque.position.y=-(frH/2)-0.18;
    grp.userData.frH=frH;
  }

  /* ---- navigation ---- */
  _focusPose(i){
    const grp=this.artGroups[i]; if(!grp) return null;
    const s=grp.userData.slot;
    let cx=s.x+s.nx*STANDOFF, cz=s.z+s.nz*STANDOFF;
    // keep the standoff point inside the room
    const b=this.bounds;
    if(b){ if(b.type==='rect'){ cx=Math.max(b.minX,Math.min(b.maxX,cx)); cz=Math.max(b.minZ,Math.min(b.maxZ,cz)); }
      else { const dx=cx-b.cx, dz=cz-b.cz, d=Math.hypot(dx,dz); if(d>b.r){ cx=b.cx+dx/d*b.r; cz=b.cz+dz/d*b.r; } } }
    const pos=new THREE.Vector3(cx,EYE,cz);
    const look=new THREE.Vector3(s.x,ART_CY-0.18,s.z);
    return {pos,look};
  }
  focusArt(i,instant){
    if(!this.artGroups.length) return;
    clearTimeout(this._estTimer);
    i=((i%this.artGroups.length)+this.artGroups.length)%this.artGroups.length;
    this.focus=i;
    window.Shop.state.focus[this.curRoom]=i; window.Shop.save();
    const pose=this._focusPose(i); if(!pose) return;
    // base orientation toward art
    const dir=pose.look.clone().sub(pose.pos).normalize();
    this._wantYaw=Math.atan2(dir.x,-dir.z); this._wantPitch=Math.asin(dir.y);
    if(instant){ this.camera.position.copy(pose.pos); this.camPos.copy(pose.pos);
      this.baseYaw=this._wantYaw; this.basePitch=this._wantPitch; this._turning=false; }
    else { this.camPos.copy(pose.pos); this._turning=true; }
    // spotlight aimed at the focused art
    const grp=this.artGroups[i], s=grp.userData.slot;
    this.spot.position.set(s.x+s.nx*1.4, WALL_H-0.4, s.z+s.nz*1.4);
    this.spot.target.position.set(s.x,ART_CY,s.z);
    // notify shop
    const arr=ART_BY_ROOM[this.curRoom]||[];
    window.Shop.showArt(grp.userData.art, arr, i);
    window.Shop.tone(window.Shop.NOTES.E,.14,'sine',.06);
  }
  step(d){ this.focusArt(this.focus+d); }

  goRoom(roomId){
    if(!ROOMS[roomId]) return;
    this.curRoom=roomId; window.Shop.state.room=roomId; window.Shop.save();
    const arr=this.buildRoom(roomId);
    window.Shop.updateTopbar(roomId);
    const savedFocus=window.Shop.state.focus[roomId]||0;
    const fi=Math.min(savedFocus,Math.max(0,arr.length-1));
    // establishing shot: stand at the entrance looking in, then settle on the artwork
    this.camera.position.set(0,EYE,2.0); this.camPos.set(0,EYE,2.0);
    this.baseYaw=Math.PI; this.basePitch=-0.04; this._wantYaw=Math.PI; this._wantPitch=-0.04; this._turning=false;
    if(arr.length){ window.Shop.showArt(arr[fi],arr,fi); this.focus=fi; }
    clearTimeout(this._estTimer);
    this._estTimer=setTimeout(()=>{ this.focusArt(fi,false); }, 1900);
    window.Shop.tone(window.Shop.NOTES.G,.22,'sine',.1);
  }

  onTheme(){ /* accent change → update neon frames */
    const theme=ROOMS[this.curRoom]; if(!theme) return;
    if(theme.frame==='neon'){ this.artGroups.forEach(g=>{ g.userData.frameMesh.material.emissive=hex(window.Shop.state.accent); }); }
  }

  /* ---- input: free-roam (drag-look + WASD/arrows + click-to-walk) ---- */
  _bindInput(){
    const el=this.canvas; let down=false,lx=0,ly=0,moved=0,downId=null;
    el.addEventListener('pointerdown',e=>{ down=true;lx=e.clientX;ly=e.clientY;moved=0;downId=e.pointerId; el.setPointerCapture(e.pointerId); });
    el.addEventListener('pointermove',e=>{ if(!down) return; const dx=e.clientX-lx,dy=e.clientY-ly; lx=e.clientX;ly=e.clientY; moved+=Math.abs(dx)+Math.abs(dy);
      this._turning=false;
      this.baseYaw -= dx*0.004;
      this.basePitch=THREE.MathUtils.clamp(this.basePitch - dy*0.003, -0.7,0.6);
    });
    const up=e=>{ if(!down) return; down=false; try{el.releasePointerCapture(downId);}catch(_){}
      if(moved<7){ this._click(e); } };
    el.addEventListener('pointerup',up); el.addEventListener('pointercancel',()=>{down=false;});
    el.addEventListener('wheel',e=>{ e.preventDefault();
      const dir=new THREE.Vector3(); this.camera.getWorldDirection(dir); dir.y=0; dir.normalize();
      this._turning=false;
      this._moveTo(this.camPos.clone().addScaledVector(dir, Math.sign(e.deltaY)*-0.8));
    },{passive:false});
    // keyboard walk
    this.keys={};
    window.addEventListener('keydown',e=>{ const k=e.key.toLowerCase();
      if(['w','a','s','d','arrowup','arrowdown','arrowleft','arrowright'].includes(k)){ this.keys[k]=true; this._turning=false; }});
    window.addEventListener('keyup',e=>{ this.keys[e.key.toLowerCase()]=false; });
  }
  // clamp a candidate xz position to the room bounds, keep eye height
  _moveTo(p){ const b=this.bounds; if(b){ if(b.type==='rect'){ p.x=Math.max(b.minX,Math.min(b.maxX,p.x)); p.z=Math.max(b.minZ,Math.min(b.maxZ,p.z)); }
      else { const dx=p.x-b.cx,dz=p.z-b.cz,d=Math.hypot(dx,dz); if(d>b.r){ p.x=b.cx+dx/d*b.r; p.z=b.cz+dz/d*b.r; } } }
    p.y=EYE; this.camPos.copy(p); }
  _click(e){
    const r=this.canvas.getBoundingClientRect();
    const nd=new THREE.Vector2(((e.clientX-r.left)/r.width)*2-1, -((e.clientY-r.top)/r.height)*2+1);
    const rc=new THREE.Raycaster(); rc.setFromCamera(nd,this.camera);
    // interactive props first
    if(this.interactives.length){ const ih=rc.intersectObjects(this.interactives,true);
      if(ih.length){ let o=ih[0].object; while(o && !o.userData.onClick) o=o.parent;
        if(o && o.userData.onClick){ o.userData.onClick(o); return; } } }
    // artwork
    const hits=rc.intersectObjects(this.artMeshes,false);
    if(hits.length){ const grp=hits[0].object.userData.artGroup; const idx=this.artGroups.indexOf(grp);
      if(idx===this.focus){ window.Shop.openDetail(grp.userData.art); } else this.focusArt(idx); return; }
    // click-to-walk: intersect the ground plane (y=0)
    const t=-rc.ray.origin.y/rc.ray.direction.y;
    if(t>0&&t<60){ const gp=rc.ray.origin.clone().addScaledVector(rc.ray.direction,t); this._turning=false; this._moveTo(gp); }
  }

  /* ---- loop ---- */
  _resize(){ const w=innerWidth,h=innerHeight; this.renderer.setSize(w,h,false); this.camera.aspect=w/h; this.camera.updateProjectionMatrix(); }
  start(){ if(!this._raf) this._animate(); }
  _animate(){
    this._raf=requestAnimationFrame(this._animate);
    const dt=Math.min(this.clock.getDelta(),0.05);
    // keyboard walk
    if(this.keys){ const k=this.keys; let fwd=(k['w']||k['arrowup']?1:0)-(k['s']||k['arrowdown']?1:0); let str=(k['d']||k['arrowright']?1:0)-(k['a']||k['arrowleft']?1:0);
      if(fwd||str){ this._turning=false; const sp=4.2*dt; const fx=Math.sin(this.baseYaw),fz=-Math.cos(this.baseYaw);
        const np=this.camPos.clone(); np.x+=fx*fwd*sp+Math.cos(this.baseYaw)*str*sp; np.z+=fz*fwd*sp+Math.sin(this.baseYaw)*str*sp; this._moveTo(np); } }
    // position tween
    this.camera.position.lerp(this.camPos,0.14);
    // orientation — ease toward focus target only while "turning"
    if(this._turning && this._wantYaw!==undefined){ this.baseYaw=lerp(this.baseYaw,this._wantYaw,0.12); this.basePitch=lerp(this.basePitch,this._wantPitch,0.12);
      if(Math.abs(this.baseYaw-this._wantYaw)<0.01) this._turning=false; }
    const yaw=this.baseYaw, pitch=this.basePitch;
    const look=new THREE.Vector3(
      this.camera.position.x+Math.sin(yaw)*Math.cos(pitch),
      this.camera.position.y+Math.sin(pitch),
      this.camera.position.z-Math.cos(yaw)*Math.cos(pitch));
    this.camera.lookAt(look);
    const t=this.clock.elapsedTime;
    if(this.starField) this.starField.rotation.z+=dt*0.01;
    if(this._causticTex){ this._causticTex.offset.y+=dt*0.02; this._causticTex.offset.x+=dt*0.01; }
    // run registered prop animations (egg float, flames, fireflies, fish, piano, chandelier…)
    for(let i=0;i<this.animated.length;i++){ try{ this.animated[i](dt,t); }catch(e){} }
    this.renderer.render(this.scene,this.camera);
  }

  _disposeRoom(){
    if(!this.roomGroup) return;
    this.roomGroup.traverse(o=>{ if(o.geometry)o.geometry.dispose(); if(o.material){ const m=o.material; (Array.isArray(m)?m:[m]).forEach(mm=>{ if(mm.map&&mm.map!==this._sharedTex)mm.map.dispose&&mm.map.dispose(); mm.dispose&&mm.dispose(); }); } });
    this.scene.remove(this.roomGroup); this.roomGroup=null; this.artGroups=[]; this.artMeshes=[];
    this.eggMesh=null; this.interactives=[]; this.animated=[]; this._causticTex=null;
  }
}

/* ==========================================================================
   BOOT
   ========================================================================== */
window.addEventListener('DOMContentLoaded',()=>{
  window.Shop.boot();
  const canvas=document.getElementById('gl');
  const gal=new Gallery3D(canvas);
  window.GAL=gal;
  window.Shop.bindEngine(gal);
  const start=window.Shop.state.room||'zen';
  gal.goRoom(start);
  gal.start();
  // auto-hide hint
  setTimeout(()=>{ const h=document.querySelector('.hint'); if(h){ h.style.transition='opacity .6s'; h.style.opacity='0'; setTimeout(()=>h.style.display='none',700);} },7000);
  // hide loader
  setTimeout(()=>{ const l=document.getElementById('boot'); if(l){ l.style.opacity='0'; setTimeout(()=>l.remove(),500); } },900);
});